import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Alert, CircularProgress,
  Card, CardContent, Divider, Chip
} from '@mui/material';
import { automationService } from '../../services/automationService';

// Note: This is an MVP translation of automation_unit.hbs. 
// A full visual builder requires a complex state machine for nodes.
const AutomationForm = () => {
  const { project, id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new' || !id;

  const [automation, setAutomation] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAutomation = async () => {
      try {
        setLoading(true);
        const data = await automationService.getAutomation(id);
        setAutomation(data);
      } catch (err) {
        console.error('Error fetching automation:', err);
        setError('Failed to load automation details.');
      } finally {
        setLoading(false);
      }
    };

    if (!isNew) {
      fetchAutomation();
    }
  }, [id, isNew]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h4" component="h1">
          {isNew ? 'Add a new Workflow' : `Edit Workflow: ${automation?.name}`}
        </Typography>
        <Button variant="outlined" onClick={() => navigate(`/project/${project}/automation`)}>
          Cancel
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Conditions</Typography>
        <Card variant="outlined" sx={{ borderStyle: 'dashed', mb: 2 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              + Add a Condition
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
              <Button variant="outlined">Page View</Button>
              <Button variant="outlined">Email</Button>
              <Button variant="outlined">User Property</Button>
              <Button variant="outlined">Custom Events</Button>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
          <Divider sx={{ flexGrow: 1 }} />
          <Chip label="OR" sx={{ mx: 2 }} />
          <Divider sx={{ flexGrow: 1 }} />
        </Box>

        <Card variant="outlined" sx={{ borderStyle: 'dashed' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              + Add a Condition
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
              <Button variant="outlined">Page View</Button>
              <Button variant="outlined">Email</Button>
              <Button variant="outlined">User Property</Button>
              <Button variant="outlined">Custom Events</Button>
            </Box>
          </CardContent>
        </Card>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Actions</Typography>
        <Card variant="outlined" sx={{ borderStyle: 'dashed' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              + Add an Action
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
              <Button variant="outlined">Send an Email</Button>
              <Button variant="outlined">Set User Property</Button>
            </Box>
          </CardContent>
        </Card>
      </Paper>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" color="success" size="large">
          Save Workflow
        </Button>
      </Box>
    </Box>
  );
};

export default AutomationForm;
